import React from 'react';
import { Typography } from '@mui/material';
import LegalLayout, { Bullets, P } from './LegalLayout';
import Seo from './Seo';

// Fixed revision date; update by hand when the terms change.
const LAST_UPDATED = '2026-07-17';

const sections = [
  {
    id: 'agreement',
    title: 'Agreement to these terms',
    content: (
      <>
        <P>
          These Terms of Service govern your use of this website and the services provided by Software Systems
          International (Pvt) Ltd ("SSIPL", "we", "us" or "our"). By accessing this website or using our services, you
          agree to be bound by these terms. If you do not agree with them, please do not use our services.
        </P>
        <P>
          Where we enter into a separate written agreement with you for a specific project or licence, that agreement
          takes precedence over these terms to the extent of any conflict.
        </P>
      </>
    )
  },
  {
    id: 'services',
    title: 'Our services',
    content: (
      <>
        <P>SSIPL provides enterprise software solutions, including but not limited to:</P>
        <Bullets
          items={[
            'POS and inventory management systems',
            'HR management systems covering core HR, time and attendance, and payroll',
            'Visitor management systems',
            'Meal supply monitoring systems',
            'Enterprise resource planning (ERP) systems',
            'Custom software development, integration and consulting services'
          ]}
        />
        <P>
          The scope, deliverables, timelines and fees for any engagement are set out in the applicable proposal or
          service agreement. Information on this website describes our capabilities in general terms and does not itself
          constitute an offer.
        </P>
      </>
    )
  },
  {
    id: 'responsibilities',
    title: 'Your responsibilities',
    content: (
      <>
        <P>When using our website or services, you agree to:</P>
        <Bullets
          items={[
            'Provide accurate and complete information where information is requested',
            'Keep your account credentials confidential and notify us promptly of any suspected unauthorised use',
            'Use our services in compliance with applicable laws and regulations',
            'Refrain from any activity that may damage, disable or impair our services or interfere with another party’s use of them',
            'Refrain from attempting to gain unauthorised access to our systems, or to probe or test their security without our prior written permission',
            'Refrain from reverse engineering, decompiling or creating derivative works from our software except to the extent that restriction is prohibited by law'
          ]}
        />
      </>
    )
  },
  {
    id: 'intellectual-property',
    title: 'Intellectual property',
    content: (
      <P>
        All content, features and functionality of our website and services, including software, source code, text,
        graphics, logos and images, are the exclusive property of SSIPL or its licensors and are protected by copyright,
        trademark and other intellectual property laws. Except where a separate agreement grants you a licence, you may
        not reproduce, distribute, modify or create derivative works from them without our express written permission.
        Any rights not expressly granted are reserved.
      </P>
    )
  },
  {
    id: 'availability',
    title: 'Service availability and changes',
    content: (
      <P>
        We work to keep our services reliable and available, but we do not warrant that they will be uninterrupted,
        error-free or entirely secure. We may modify, suspend or discontinue any part of our services, and we may carry
        out maintenance that causes temporary unavailability. Where a service level commitment applies to you, it is set
        out in your service agreement and takes precedence over this section.
      </P>
    )
  },
  {
    id: 'payment',
    title: 'Fees and payment',
    content: (
      <P>
        Fees, billing cycles and payment terms are specified in the applicable service agreement or invoice. Unless
        stated otherwise, fees are exclusive of taxes and are non-refundable once the corresponding work has been
        delivered. Late or non-payment may result in suspension or termination of services after we have given you
        reasonable notice.
      </P>
    )
  },
  {
    id: 'privacy',
    title: 'Privacy and data protection',
    content: (
      <P>
        Our handling of personal data is described in our Privacy Policy, which forms part of these terms. Where we
        process personal data on your behalf as part of delivering a service, we do so as a processor acting on your
        documented instructions under the data processing terms in your service agreement.
      </P>
    )
  },
  {
    id: 'liability',
    title: 'Limitation of liability',
    content: (
      <>
        <P>
          To the fullest extent permitted by law, SSIPL shall not be liable for any indirect, incidental, special,
          consequential or punitive damages, or for any loss of profits, revenue, data, use or goodwill, arising out of
          or relating to:
        </P>
        <Bullets
          items={[
            'Your use of, or inability to use, our website or services',
            'Unauthorised access to or alteration of your data by a third party',
            'The conduct or content of any third party using our services'
          ]}
        />
        <P>
          Where liability cannot lawfully be excluded, our total aggregate liability arising out of or relating to the
          services is limited to the fees you paid to us for the service giving rise to the claim in the 12 months
          preceding the event. Nothing in these terms excludes liability for fraud, wilful misconduct, death or personal
          injury caused by negligence, or any other liability that cannot be excluded under Sri Lankan law.
        </P>
      </>
    )
  },
  {
    id: 'termination',
    title: 'Termination',
    content: (
      <P>
        We may suspend or terminate your access to our services if you materially breach these terms, if required by law,
        or to protect the security of our systems or other users. Where circumstances reasonably permit, we will give you
        notice and an opportunity to remedy the breach. On termination, your right to use the services ceases
        immediately. Terms that by their nature should survive termination, including intellectual property, limitation
        of liability and governing law, will continue to apply.
      </P>
    )
  },
  {
    id: 'governing-law',
    title: 'Governing law',
    content: (
      <P>
        These terms are governed by and construed in accordance with the laws of Sri Lanka, without regard to its
        conflict of law provisions. Any dispute arising out of or relating to these terms is subject to the exclusive
        jurisdiction of the courts of Sri Lanka. If any provision of these terms is found unenforceable, the remaining
        provisions continue in full force.
      </P>
    )
  },
  {
    id: 'changes',
    title: 'Changes to these terms',
    content: (
      <P>
        We may modify these terms from time to time. The revision date at the top of this page shows when they last
        changed, and we will post the updated terms here. Where a change is material, we will take reasonable steps to
        notify you. Your continued use of our services after the change takes effect constitutes acceptance of the
        updated terms.
      </P>
    )
  },
  {
    id: 'contact',
    title: 'Contact us',
    content: (
      <P>
        If you have any question about these terms, or would like clarification before entering into an agreement with
        us, contact us using the details below. We aim to reply within two business days.
      </P>
    )
  }
];

const jsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Terms of Service',
  url: 'https://ssipl.lk/terms',
  description: 'The terms governing use of the SSIPL website and services.',
  dateModified: LAST_UPDATED,
  isPartOf: { '@id': 'https://ssipl.lk/#website' }
};

const TermsPage = () => (
  <>
    <Seo
      title="Terms of Service | SSIPL"
      description="The terms governing your use of the Software Systems International (Pvt) Ltd website and our enterprise software services, including fees, liability and governing law."
      path="/terms"
      jsonLd={jsonLd}
    />
    <LegalLayout
      eyebrow="Legal"
      title="Terms of Service"
      intro={
        <Typography component="span" color="text.secondary" sx={{ lineHeight: 1.8 }}>
          These terms explain the rules for using our website and services, what you can expect from us, and what we ask
          of you in return.
        </Typography>
      }
      lastUpdated={LAST_UPDATED}
      sections={sections}
    />
  </>
);

export default TermsPage;
